import React, { useState } from 'react';
import { submitFeedback, FeedbackType } from '../../services/api';
import './FeedbackWidget.css';

interface FeedbackWidgetProps {
  questionId: string;
  studentId: string;
}

const FEEDBACK_OPTIONS: { type: FeedbackType; label: string }[] = [
  { type: 'unclear', label: 'Explanation unclear' },
  { type: 'answer_wrong', label: 'I think my answer was correct' },
  { type: 'technical', label: 'Technical problem' },
  { type: 'other', label: 'Other issue' },
];

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  questionId,
  studentId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [helpful, setHelpful] = useState<boolean | null>(null);

  const handleThumbsDown = () => {
    setHelpful(false);
    setExpanded(true);
  };

  const handleThumbsUp = () => {
    setHelpful(true);
    // Just mark as helpful, no further action needed
  };

  const handleSubmit = async () => {
    if (!selectedType) return;

    setSubmitting(true);
    try {
      await submitFeedback(studentId, {
        question_id: questionId,
        feedback_type: selectedType,
        description: description.trim() || undefined,
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Still show success to avoid frustrating the user
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Already submitted
  if (submitted) {
    return (
      <div className="feedback-widget submitted">
        <span className="feedback-thanks">Thanks for your feedback!</span>
      </div>
    );
  }

  // Marked as helpful
  if (helpful === true) {
    return (
      <div className="feedback-widget submitted">
        <span className="feedback-thanks">Glad it helped!</span>
      </div>
    );
  }

  // Initial state - show thumbs up/down
  if (!expanded) {
    return (
      <div className="feedback-widget">
        <span className="feedback-prompt">Was this explanation helpful?</span>
        <div className="feedback-thumbs">
          <button
            className="thumb-btn thumb-up"
            onClick={handleThumbsUp}
            aria-label="Yes, helpful"
          >
            <span role="img" aria-hidden="true">&#128077;</span>
          </button>
          <button
            className="thumb-btn thumb-down"
            onClick={handleThumbsDown}
            aria-label="No, not helpful"
          >
            <span role="img" aria-hidden="true">&#128078;</span>
          </button>
        </div>
      </div>
    );
  }

  // Expanded state - show feedback options
  return (
    <div className="feedback-widget expanded">
      <div className="feedback-header">
        <span className="feedback-title">What went wrong?</span>
        <button
          className="feedback-close"
          onClick={() => setExpanded(false)}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className="feedback-options">
        {FEEDBACK_OPTIONS.map((option) => (
          <button
            key={option.type}
            className={`feedback-option ${selectedType === option.type ? 'selected' : ''}`}
            onClick={() => setSelectedType(option.type)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {selectedType && (
        <div className="feedback-details">
          <textarea
            className="feedback-textarea"
            placeholder="Add details (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <button
            className="feedback-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Send Feedback'}
          </button>
        </div>
      )}
    </div>
  );
};
